import type { ApiClient } from '@carbonwebmail/api-client'
import { useNavigate, useSearchParams } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { Dialog } from '../../ui/Dialog.js'
import {
  AudienceStep,
  MessageStep,
  PurposeStep,
  ReviewStep,
  SubjectStep,
} from './NewCampaignSteps.js'
import { STEP_TITLES, STEPS, useNewCampaign } from './useNewCampaign.js'

/**
 * Writing a campaign, as a sequence rather than a form.
 *
 * **A dialog on its own route.** It is a dialog because it is one job with a
 * beginning and an end, and nothing underneath it is worth looking at while it
 * is open. It keeps a route because three different places link to it — the
 * sidebar, the campaigns list, and a template's "Use" button with the template
 * in the query — and a dialog opened only by local state would need each of
 * those to know how to reach it.
 *
 * **The button at the end says "Create draft", not "Send".** Sending is a
 * separate press on the campaign itself, because the two decisions are
 * different sizes: what to say, and whether to say it to several thousand
 * people. Closing loses the draft, which is why the last step shows the whole
 * of it before anything is written.
 */
export function NewCampaignPage({ client }: { readonly client: ApiClient }) {
  const navigate = useNavigate()
  // A template chosen from its card in the gallery, rather than from the
  // picker on the message step. Both end in the same place.
  const [query] = useSearchParams()

  const wizard = useNewCampaign({
    client,
    fromTemplate: query.get('template') ?? undefined,
    // Into the composer, not back to the list. The wizard has just collected a
    // whole message and nobody has seen what it looks like — and a person
    // returned to a list has to find their way back to the one screen that
    // shows them.
    onCreated: (campaignId) => void navigate(`/campaigns/${campaignId}/edit`),
  })

  const { draft, step, at, blocked, problems, saving, back, next, goTo, create } = wizard
  const last = at === STEPS.length - 1

  /**
   * Leaving, and asking first when there is something to lose.
   *
   * A dialog closes on its scrim and on Escape, which is right for a question
   * and wrong for five steps of work: a stray click beside the panel threw
   * away a brief, a name, an audience and a subject with no warning. Caught
   * exactly that way on 12 Oct, a minute after the wizard first deployed.
   *
   * Asked only when something has been typed. A prompt on every exit is one
   * people learn to dismiss, and then it does not protect the time it was for
   * — the same reasoning, and the same `window.confirm`, as leaving the
   * composer with unsaved changes.
   */
  const started = draft.brief.trim() !== '' || draft.name.trim() !== ''

  const leave = () => {
    if (started && !window.confirm(LEAVING)) return
    void navigate('/campaigns')
  }

  return (
    <Dialog
      wide
      heading="New campaign"
      description={`Step ${at + 1} of ${STEPS.length} · ${STEP_TITLES[step]}`}
      onClose={leave}
      footer={
        <>
          {at === 0 ? null : (
            <button
              type="button"
              onClick={back}
              disabled={saving}
              className="rounded-pill border border-line px-4 py-2 font-medium text-[13px] text-t2 hover:text-t1 disabled:opacity-60"
            >
              Back
            </button>
          )}

          {/*
            The reason sits beside the button rather than under the field it
            came from. A disabled Next with nothing next to it is the control
            people press twice and then close the dialog.
          */}
          {blocked === undefined ? null : (
            <p className="order-last w-full text-[12px] text-t3 sm:order-none sm:w-auto">
              {blocked}
            </p>
          )}

          <button
            type="button"
            disabled={blocked !== undefined || saving}
            onClick={() => (last ? void create() : next())}
            className="ml-auto rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
          >
            {last ? (saving ? 'Creating…' : 'Create draft') : 'Next'}
          </button>
        </>
      }
    >
      {/*
        The progress strip. Steps already passed are links back; the ones ahead
        are not, because they ask questions whose answers depend on ones that
        have not been given — the subject suggestions come from the brief, and
        at step one there is no brief.
      */}
      <ol className="mb-6 flex gap-2">
        {STEPS.map((one, index) => {
          const done = index < at
          return (
            <li key={one} className="flex-1">
              <button
                type="button"
                disabled={!done}
                onClick={() => goTo(one)}
                title={STEP_TITLES[one]}
                className={`h-1.5 w-full rounded-pill transition-colors ${
                  index <= at ? 'bg-brand' : 'bg-line'
                } ${done ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <span className="sr-only">
                  {STEP_TITLES[one]}
                  {done ? ' — go back to this step' : ''}
                </span>
              </button>
            </li>
          )
        })}
      </ol>

      {problems.length === 0 ? null : (
        <Alert>
          <ul>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Alert>
      )}

      {step === 'purpose' ? <PurposeStep wizard={wizard} /> : null}
      {step === 'audience' ? <AudienceStep wizard={wizard} /> : null}
      {step === 'subject' ? <SubjectStep wizard={wizard} /> : null}
      {step === 'message' ? <MessageStep wizard={wizard} /> : null}
      {step === 'review' ? <ReviewStep wizard={wizard} /> : null}
    </Dialog>
  )
}

const LEAVING = 'This campaign has not been created yet. Leave and lose what you have written?'
